import { useContext, memo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GradientButton } from '../../gradients/GradientButton';
import { useTournamentRegistrationStatus } from 'src/hooks/tournaments/useTournamentRegistrationStatus';
import { NUMERIC_TIMEZONE } from 'src/util/dates/presets/times';
import { useDateFormatter } from 'src/hooks/useDateFormatter';
import { TournamentContext } from 'src/contexts/docs/TournamentContext';
import Collapse from '@mui/material/Collapse';
import { useHover } from '../../../contexts/HoverContext';
import Stack from '@mui/system/Stack';
import CircleIcon from '@mui/icons-material/Circle';
import { useMobile } from '../../../contexts/useMobile';

const DEFAULT_CONFIG = { color: 'primary', label: 'View' } as const;
const BUTTON_CONFIG = {
  'not-registered': { color: 'secondary', label: 'Register' },
  registered: { color: 'error', label: 'Unregister' },
  'checked-in': { color: 'primary', label: 'Check in' },
  unpublished: { color: 'warning', label: 'Edit' },
} as const;

const TournamentCardActionsUnmemoized: React.FC = () => {
  const { isHovered } = useHover();
  const isMobile = useMobile();
  const theme = useTheme();

  const {
    readyTime,
    date,
    endDate,
    phase,
    guestlistAggregated,
    waitlistAggregated,
  } = useContext(TournamentContext);

  const { determineStatus } = useTournamentRegistrationStatus();

  const status = determineStatus(guestlistAggregated, waitlistAggregated);

  const { color, label } =
    phase === 'live' || phase === 'finished'
      ? DEFAULT_CONFIG
      : phase === 'unpublished'
      ? BUTTON_CONFIG['unpublished']
      : BUTTON_CONFIG[String(status)];

  const format = useDateFormatter({
    dateFormat: false,
    timeFormat: NUMERIC_TIMEZONE,
    separator: ' ',
  });

  const getPhaseTextAndTime = () => {
    if (phase === 'ready') {
      return { timePrefix: 'STARTS AT', time: date };
    }
    if (phase === 'live') {
      return { timePrefix: 'LIVE SINCE', time: date };
    }
    if (phase === 'finished') {
      return { timePrefix: 'ENDED AT', time: endDate };
    }
    return { timePrefix: 'CHECK-IN BY', time: readyTime };
  };
  const { timePrefix, time } = getPhaseTextAndTime();

  return (
    <Stack>
      <Box
        borderRadius="5px"
        padding={1}
        sx={{
          background: theme.palette.background.elevation[15],
        }}
      >
        <Typography
          sx={{
            fontWeight: '600',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
          }}
        >
          {phase === 'live' && (
            <CircleIcon
              sx={{
                fontSize: '1rem',
                color: theme.palette.error.main,
                verticalAlign: '-2px',
                marginRight: 1,
              }}
            />
          )}
          {phase === 'unpublished'
            ? 'Unpublished'
            : `${timePrefix}: ${format(time)}`}
        </Typography>
      </Box>

      <Collapse in={isMobile || isHovered}>
        <GradientButton
          color={color}
          sx={{
            mt: 2,
            width: '100%',
          }}
        >
          {label}
        </GradientButton>
      </Collapse>
    </Stack>
  );
};

export const TournamentCardActions = memo(TournamentCardActionsUnmemoized);
